import { useEffect, useState } from "react";
import type { ReposResponse } from "./repoTypes";

export type ReposStatus = "loading" | "ready" | "error";

export interface ReposState {
  status: ReposStatus;
  repos: ReposResponse | null;
  message: string | null;
}

// useRepos fetches GET /api/repos once a token is available — the one meta
// endpoint that lists every configured repo, so it deliberately doesn't go
// through useApiResource (whose apiPath always builds /api/repos/{repo}/...;
// this endpoint has no {repo} segment at all, it's what the repo segment
// comes from). On failure this renders the backend's own sanitized message
// rather than silently falling back to a hardcoded repo list — nav
// correctness is load-bearing (a wrong/stale nav silently offers links that
// 404), so it deserves the same "never fabricate, show the real error"
// treatment as every other reader in this app.
export function useRepos(token: string | undefined): ReposState {
  const [status, setStatus] = useState<ReposStatus>("loading");
  const [repos, setRepos] = useState<ReposResponse | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    let active = true;
    setStatus("loading");
    fetch("/api/repos", { headers: { Authorization: `Bearer ${token}` } })
      .then(async (res) => {
        if (!active) return;
        if (!res.ok) {
          const body = (await res.json().catch(() => ({}))) as { error?: string };
          setStatus("error");
          setMessage(body.error ?? `HTTP ${res.status}`);
          return;
        }
        const body = (await res.json()) as ReposResponse;
        setRepos(body);
        setStatus("ready");
      })
      .catch((e: unknown) => {
        if (!active) return;
        setStatus("error");
        setMessage(e instanceof Error ? e.message : "unknown error");
      });
    return () => {
      active = false;
    };
  }, [token]);

  return { status, repos, message };
}
